/*
 * The one way the application talks HTTP. Everything about a call that must not be decided twice
 * lives here: the JSON content type, `no-store` so a back button never shows yesterday's messages,
 * the CSRF header on every mutating request, and the single error type the rest of the code
 * catches (protocol.md §3.1, §1.1).
 *
 * The session is a `__Host-` cookie the browser sends by itself and JavaScript never sees. The CSRF
 * token is the opposite: it is held in memory only — `GET /api/auth/session` re-delivers it, so all
 * tabs of one session hold the same value — and before a session exists the four anonymous routes
 * double-submit the `_csrf` cookie instead (security.md §5.1, §5.2).
 */

import { isKey, type Key } from "../i18n/index.ts"

export const CSRF_HEADER = "X-TeamBase-CSRF"

/**
 * The pre-session double-submit cookie is `__Host-<p>_csrf`, where `<p>` is the server's
 * `[auth] cookie_prefix` — `tb` by default, but `tb1`/`tb2` on a worktree stack, because cookies
 * ignore the port and two localhost stacks would otherwise clobber each other; a local testing
 * instance may drop the `__Host-` prefix altogether (security.md §5.1). The client therefore matches
 * the shape rather than one literal name, and never has to be told which stack it is served from.
 */
const CSRF_COOKIE_PATTERN = /^(__Host-)?[a-z][a-z0-9]{0,7}_csrf$/

/** The error shape of protocol.md §1.1, as it arrives inside `{"error": …}`. */
export interface WirePayload {
	code: string
	key: string
	args?: Record<string, string | number>
	retryAfterMs?: number
	detail?: unknown
	requestId?: string
}

/**
 * Every failed call throws this — a refused request, a rejected body, a dropped connection. `key`
 * is the server's locale key; `messageKey` is the one to render, which falls back to the code and
 * then to a generic message so an older client never shows a raw key at a person.
 */
export class WireError extends Error {
	readonly status: number
	readonly code: string
	readonly key: string
	readonly args: Record<string, string | number> | undefined
	readonly retryAfterMs: number | undefined
	readonly detail: unknown
	readonly requestId: string | undefined

	constructor(status: number, payload: WirePayload) {
		super(`${payload.code}: ${payload.key}`)
		this.name = "WireError"
		this.status = status
		this.code = payload.code
		this.key = payload.key
		this.args = payload.args
		this.retryAfterMs = payload.retryAfterMs
		this.detail = payload.detail
		this.requestId = payload.requestId
	}

	/** The catalogue key to render: the server's, else one per code, else the generic one. */
	get messageKey(): Key {
		if (isKey(this.key)) return this.key
		const byCode = `error.${this.code}`
		return isKey(byCode) ? byCode : "error.unknown"
	}

	/** True while retrying the same call unchanged could succeed. */
	get retryable(): boolean {
		return this.code === "rateLimited" || this.code === "unavailable" || this.status >= 500
	}
}

/**
 * What a refused call says, as a catalogue key: the server's own sentence where this build knows
 * it, and the generic one for anything that is not a wire error at all — a dropped connection, a
 * bug in a handler.
 *
 * It is here and not in each screen because the expression is the same everywhere and the fallback
 * is a decision, not a detail: eight places had written it out, three of them with the same private
 * helper under the same doc comment.
 */
export function messageKeyOf(error: unknown): Key {
	return error instanceof WireError ? error.messageKey : "error.unknown"
}

let csrfToken = ""

/** Called with the token every `GET /api/auth/session` answers, and with "" on sign-out. */
export function setCsrfToken(token: string): void {
	csrfToken = token
}

/**
 * The value of the first cookie whose name matches and whose value is not empty. An empty value is
 * skipped rather than returned: a cleared cookie is no cookie, and sending "" as the double-submit
 * token would be refused as a CSRF failure instead of falling through to the next candidate.
 */
function cookieMatching(pattern: RegExp): string {
	for (const part of document.cookie.split("; ")) {
		const eq = part.indexOf("=")
		if (eq <= 0 || !pattern.test(part.slice(0, eq))) continue
		const value = decodeURIComponent(part.slice(eq + 1))
		if (value !== "") return value
	}
	return ""
}

/**
 * The session's token, or the pre-session double-submit cookie for the anonymous routes.
 *
 * It is exported because one request cannot go through `request` below and still has to carry the
 * same value: the upload transport is XHR, for its progress events, and the copy of the token it
 * kept was a second thing that could be stale while this one was not (I43). One cache, one rule
 * about what is in it.
 */
export function csrfValue(): string {
	return csrfToken !== "" ? csrfToken : cookieMatching(CSRF_COOKIE_PATTERN)
}

async function parseError(response: Response): Promise<WireError> {
	try {
		const body = (await response.json()) as { error?: WirePayload }
		if (body.error) return new WireError(response.status, body.error)
	} catch {
		// A response without a JSON body (a proxy's 502, a truncated answer) still needs a shape.
	}
	return new WireError(response.status, { code: "internal", key: "error.internal" })
}

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
	const headers: Record<string, string> = { Accept: "application/json" }
	if (body !== undefined) headers["Content-Type"] = "application/json; charset=utf-8"
	if (method !== "GET") headers[CSRF_HEADER] = csrfValue()

	let response: Response
	try {
		response = await fetch(path, {
			method,
			headers,
			cache: "no-store",
			credentials: "same-origin",
			...(body === undefined ? {} : { body: JSON.stringify(body) }),
		})
	} catch {
		// A refused or aborted fetch is indistinguishable from being offline; say so, and let the
		// caller decide whether to retry.
		throw new WireError(0, { code: "network", key: "error.network" })
	}

	if (!response.ok) throw await parseError(response)
	if (response.status === 204) return undefined as T
	return (await response.json()) as T
}

/**
 * A GET whose answer is a file rather than JSON — a document's export — answered as the response
 * itself, so the caller reads its headers and its bytes. A refusal throws the same error every other
 * call throws.
 */
async function file(path: string): Promise<Response> {
	let response: Response
	try {
		response = await fetch(path, { method: "GET", cache: "no-store", credentials: "same-origin" })
	} catch {
		throw new WireError(0, { code: "network", key: "error.network" })
	}
	if (!response.ok) throw await parseError(response)
	return response
}

/**
 * The HTTP surface. Paths are absolute and start with "/api"; a GET never mutates, and every other
 * verb carries the CSRF header.
 */
export const api = {
	get: <T>(path: string): Promise<T> => request<T>("GET", path),
	post: <T>(path: string, body?: unknown): Promise<T> => request<T>("POST", path, body ?? {}),
	put: <T>(path: string, body?: unknown): Promise<T> => request<T>("PUT", path, body ?? {}),
	patch: <T>(path: string, body?: unknown): Promise<T> => request<T>("PATCH", path, body ?? {}),
	del: <T>(path: string): Promise<T> => request<T>("DELETE", path),
	file,
}
